import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import { useAutoGrowTextarea } from '../../../../hooks/useAutoGrowTextarea';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import { DmShellLayout } from './DmShellLayout';
import { SuccessBanner, ErrorBanner, BlockedNotLinkedBanner } from './DmBanners';
import { SkeletonCard } from './DmSkeletons';
import { dmInputClasses, DmBtnPrimary, DmBtnSecondary } from './DmFormPrimitives';
import { NotesSyncStatus } from './notes/NotesSyncStatus';
import { useDmNotesEditor } from './notes/useDmNotesEditor';
import type { EmbeddableActionRunnerProps } from '../types';

const SAVED_BANNER_MS = 3000;

// Embedded, the notes fill the tab above a pinned footer; the text area grows and the shared
// scroll area around it scrolls, so the tab gets the app's overlay scrollbar.
const LAYOUT = {
    embedded: {
        body: 'flex flex-col flex-1 min-h-0',
        state: 'p-4',
        banners: 'px-4 pt-4 flex-shrink-0',
        // Not flex-1: its 0% basis overrides the auto-grown height and clips the text unscrollably.
        textarea:
            'grow shrink-0 w-full resize-none px-4 py-3 text-sm leading-relaxed text-gray-900 outline-none placeholder:text-gray-300 read-only:text-gray-500',
        footer: 'px-4 py-3 border-t border-gray-200'
    },
    page: {
        body: 'w-full flex flex-col h-full gap-4',
        state: '',
        banners: '',
        textarea: `${dmInputClasses} flex-1 resize-none leading-relaxed min-h-[400px] read-only:text-gray-500`,
        footer: ''
    }
} as const;

// Keyed by patient so a draft, save result or banner never carries over to another patient.
function DmNotesLanding(props: EmbeddableActionRunnerProps) {
    return <DmNotesEditorPanel key={props.patientId} {...props} />;
}

function DmNotesEditorPanel({ patientId, onBack, embedded = false }: EmbeddableActionRunnerProps) {
    const { data: card, isLoading: cardLoading } = usePatientCard(patientId);
    const editor = useDmNotesEditor(patientId);

    const isLinked = card?.monitoring?.dmId != null;
    const isLoading = cardLoading || (isLinked && editor.isLoading);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    // Only the embedded text area takes the ref; uncapped, the scroll area around it scrolls.
    useAutoGrowTextarea(textareaRef, editor.text, Number.MAX_SAFE_INTEGER);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!saved) return;
        const t = setTimeout(() => setSaved(false), SAVED_BANNER_MS);
        return () => clearTimeout(t);
    }, [saved]);

    // Embedded there is nowhere to leave to, so Cancel discards the edits instead.
    const handleCancel = embedded ? editor.discard : onBack;
    const handleSave = () => {
        setSaved(false);
        editor.save(() => setSaved(true));
    };
    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setSaved(false);
        editor.edit(e.target.value);
    };

    const layout = embedded ? LAYOUT.embedded : LAYOUT.page;
    const textarea = (
        <textarea
            ref={embedded ? textareaRef : undefined}
            value={editor.text}
            onChange={handleChange}
            readOnly={editor.readOnly}
            aria-busy={editor.liveStatus === 'checking'}
            placeholder="Enter DM notes…"
            className={layout.textarea}
        />
    );

    return (
        <DmShellLayout title="Edit DM notes" onBack={onBack} embedded={embedded} bleed={embedded}>
            <div className={layout.body}>
                {isLoading && (
                    <div className={layout.state}>
                        <SkeletonCard />
                    </div>
                )}

                {!isLoading && !isLinked && (
                    <div className={layout.state}>
                        <BlockedNotLinkedBanner onBack={onBack} />
                    </div>
                )}

                {!isLoading && isLinked && (
                    <>
                        {(saved || editor.saveError) && (
                            <div className={layout.banners}>
                                {saved && <SuccessBanner message="Notes saved." />}
                                {editor.saveError && (
                                    <ErrorBanner
                                        message={editor.saveError}
                                        onRetry={editor.clearSaveError}
                                    />
                                )}
                            </div>
                        )}

                        {embedded ? (
                            <OverlayScrollArea
                                className="flex-1 min-h-0"
                                innerClassName="min-h-full flex flex-col"
                                trackInsetPx={6}
                            >
                                {textarea}
                            </OverlayScrollArea>
                        ) : (
                            textarea
                        )}

                        <div
                            className={`flex items-center justify-between gap-2 flex-shrink-0 ${layout.footer}`}
                        >
                            <NotesSyncStatus status={editor.liveStatus} />
                            <div className="flex gap-2 ml-auto">
                                <DmBtnSecondary
                                    onClick={handleCancel}
                                    disabled={editor.saving || (embedded && !editor.isDirty)}
                                >
                                    {embedded ? 'Discard' : 'Cancel'}
                                </DmBtnSecondary>
                                <DmBtnPrimary
                                    onClick={handleSave}
                                    disabled={!editor.isDirty || editor.saving}
                                >
                                    {editor.saving ? 'Saving…' : 'Save notes'}
                                </DmBtnPrimary>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </DmShellLayout>
    );
}

export { DmNotesLanding };
